import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Layers,
  List as ListIcon,
  Pencil,
  Plus,
  Search,
  Table as TableIcon,
  Trash2,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PermissionDenied } from '@/components/ui/permission-denied';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useSession } from '@/stores/session';

import { AgentTile } from '../components/AgentTile';
import { InstallStatusBadge, PrivateLock } from '../components/AgentStatusBadge';
import { InstallFormDialog } from '../components/InstallFormDialog';
import { DIRECTION_LABELS, DIRECTION_ORDER } from '../constants';
import { useAgentInstalls, useAgentTemplates, useDeleteAgentInstall } from '../queries';
import { useAgentsUi, type AgentsViewMode } from '../store/viewPrefs';
import type { AgentDirection, AgentInstall, AgentTemplate } from '../types';

/** One template row + its install state (or an Add action if not on the team). */
interface Row {
  template: AgentTemplate;
  install: AgentInstall | null;
}

const VIEW_MODES: readonly ViewModeOption<AgentsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by direction', icon: Layers },
];

/** Shared row-action handlers. View opens the detail page; Add/Edit open the
 *  install dialog; Delete stages the remove confirmation. */
interface RowActions {
  onView: (row: Row) => void;
  onInstall: (template: AgentTemplate) => void;
  onEdit: (template: AgentTemplate, install: AgentInstall) => void;
  onDelete: (install: AgentInstall) => void;
}

/** Stop a nested control (View/Add) from also triggering the card's row click. */
function stop(e: React.MouseEvent) {
  e.stopPropagation();
}

/** Make a non-button element behave like a clickable "open" control (mouse +
 *  keyboard), so a whole card/row can navigate to the detail page. */
function openProps(onOpen: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    },
  };
}

/**
 * Agents admin — manage the org's installs. Founder / Super Admin only; the
 * route + nav are gated, and the API enforces 403 on writes — this page is the
 * client-side defense-in-depth check (mirrors ApiKeysPage).
 */
export function AgentsAdminPage() {
  const role = useSession((s) => s.role);
  const canManage = role === 'founder' || role === 'super_admin';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Agents</h1>
        <p className="text-muted-foreground text-sm">
          Add agents to your team and configure how they run — version, tools, and knowledge-base
          scope. Staff see the team under AI Team.
        </p>
      </div>

      {canManage ? <AgentsAdminBody /> : <PermissionDenied />}
    </div>
  );
}

function AgentsAdminBody() {
  const navigate = useNavigate();
  const templatesQuery = useAgentTemplates();
  const installsQuery = useAgentInstalls();
  const deleteInstall = useDeleteAgentInstall();

  const [dialogTemplate, setDialogTemplate] = useState<AgentTemplate | null>(null);
  const [dialogInstall, setDialogInstall] = useState<AgentInstall | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<AgentInstall | null>(null);

  const view = useAgentsUi((s) => s.view);
  const setView = useAgentsUi((s) => s.setView);

  // Client-side filters — the roster is small and already fully loaded, so
  // search + the direction filter run in-memory (no server params).
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim().toLowerCase(), 300);
  const [direction, setDirection] = useState('all');
  const [membership, setMembership] = useState<'all' | 'on' | 'off'>('all');

  const rows: Row[] = useMemo(() => {
    const templates = templatesQuery.data ?? [];
    const installs = installsQuery.data ?? [];
    const byTemplate = new Map<number, AgentInstall>();
    for (const i of installs) byTemplate.set(i.template, i);
    return templates.map((template) => ({
      template,
      install: byTemplate.get(template.id) ?? null,
    }));
  }, [templatesQuery.data, installsQuery.data]);

  // Apply the toolbar filters — name/code search + direction + membership.
  const filteredRows = useMemo(() => {
    return rows.filter(({ template, install }) => {
      if (direction !== 'all' && template.direction !== direction) return false;
      if (membership === 'on' && !install) return false;
      if (membership === 'off' && install) return false;
      if (!search) return true;
      return (
        template.name.toLowerCase().includes(search) ||
        template.code.toLowerCase().includes(search)
      );
    });
  }, [rows, direction, search, membership]);

  // Group rows by direction in canonical order — mirrors TeamRoster's grouping.
  const grouped = useMemo(() => {
    const byDirection = new Map<AgentDirection, Row[]>();
    for (const row of filteredRows) {
      const list = byDirection.get(row.template.direction) ?? [];
      list.push(row);
      byDirection.set(row.template.direction, list);
    }
    return DIRECTION_ORDER.filter((d) => byDirection.has(d)).map((d) => ({
      direction: d,
      rows: byDirection.get(d)!,
    }));
  }, [filteredRows]);

  const openInstall = (template: AgentTemplate) => {
    setDialogInstall(null);
    setDialogTemplate(template);
    setDialogOpen(true);
  };

  const openEdit = (template: AgentTemplate, install: AgentInstall) => {
    setDialogTemplate(template);
    setDialogInstall(install);
    setDialogOpen(true);
  };

  const openView = (row: Row) => {
    navigate(`/settings/agents/${row.template.code}`);
  };

  const actions: RowActions = {
    onView: openView,
    onInstall: openInstall,
    onEdit: openEdit,
    onDelete: setPendingDelete,
  };

  const isPending = templatesQuery.isPending || installsQuery.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or code…"
              className="pl-8"
              aria-label="Search agents"
            />
          </div>
          <Select value={direction} onValueChange={setDirection}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All directions</SelectItem>
              {DIRECTION_ORDER.map((d) => (
                <SelectItem key={d} value={d}>
                  {DIRECTION_LABELS[d]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={membership} onValueChange={(v) => setMembership(v as typeof membership)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All agents</SelectItem>
              <SelectItem value="on">On team</SelectItem>
              <SelectItem value="off">Not on team</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <ViewModeToggle
          value={view}
          onChange={setView}
          modes={VIEW_MODES}
          ariaLabel="Agents view"
        />
      </div>

      {isPending ? (
        <AgentsSkeleton />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          No agent templates are available in the catalog.
        </p>
      ) : filteredRows.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          No agents match your filters.
        </p>
      ) : view === 'table' ? (
        <AgentsTable rows={filteredRows} actions={actions} />
      ) : view === 'list' ? (
        <AgentsList rows={filteredRows} actions={actions} />
      ) : view === 'cards' ? (
        <AgentsCards rows={filteredRows} actions={actions} />
      ) : (
        <AgentsGroupedByDirection groups={grouped} actions={actions} />
      )}

      <InstallFormDialog
        key={`${dialogInstall?.id ?? dialogTemplate?.id ?? 'none'}:${dialogOpen}`}
        template={dialogTemplate}
        install={dialogInstall}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      <AlertDialog open={pendingDelete !== null} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove “{pendingDelete?.template_name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the agent from your team. Staff will no longer see it in the AI Team
              directory or be able to run it. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                if (pendingDelete) deleteInstall.mutate(pendingDelete.public_id);
                setPendingDelete(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

/**
 * Row/card actions: "Add to team" when the agent isn't on the team; quick Edit +
 * Remove icons when it is. Each stops propagation so it doesn't also fire the
 * card/row click (which opens the detail page).
 */
function AgentActions({
  template,
  install,
  actions,
}: {
  template: AgentTemplate;
  install: AgentInstall | null;
  actions: RowActions;
}) {
  if (!install) {
    return (
      <Button
        size="sm"
        onClick={(e) => {
          stop(e);
          actions.onInstall(template);
        }}
      >
        <Plus className="size-4" /> Add to team
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Button
        size="icon"
        variant="ghost"
        aria-label={`Edit ${template.name}`}
        onClick={(e) => {
          stop(e);
          actions.onEdit(template, install);
        }}
      >
        <Pencil className="size-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        aria-label={`Remove ${template.name}`}
        className="text-muted-foreground hover:text-destructive"
        onClick={(e) => {
          stop(e);
          actions.onDelete(install);
        }}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
}

/** Flat table — Agent / Direction / On team / Enabled / Actions. */
function AgentsTable({ rows, actions }: { rows: Row[]; actions: RowActions }) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Agent</TableHead>
            <TableHead className="hidden sm:table-cell">Direction</TableHead>
            <TableHead>On team</TableHead>
            <TableHead className="hidden md:table-cell">Enabled</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ template, install }) => (
            <TableRow
              key={template.id}
              className="cursor-pointer"
              onClick={() => actions.onView({ template, install })}
            >
              <TableCell className="max-w-xs">
                <div className="flex items-center gap-3">
                  <AgentTile code={template.code} direction={template.direction} imageUrl={template.image} className="size-9 text-xs" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium">{template.name}</span>
                      {install ? <PrivateLock scope={install.access_scope} /> : null}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      <code>{template.code}</code>
                    </div>
                    {template.summary ? (
                      <div className="text-muted-foreground line-clamp-1 text-xs">
                        {template.summary}
                      </div>
                    ) : null}
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <Badge variant="outline" className="font-normal">
                  {DIRECTION_LABELS[template.direction]}
                </Badge>
              </TableCell>
              <TableCell>
                {install ? (
                  <InstallStatusBadge status={install.status} />
                ) : (
                  <span className="text-muted-foreground text-sm">Not on team</span>
                )}
              </TableCell>
              <TableCell className="hidden text-sm md:table-cell">
                {install ? (install.enabled ? 'Yes' : 'No') : '—'}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <AgentActions template={template} install={install} actions={actions} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Dense single-column list — the row body opens the agent; actions sit apart. */
function AgentsList({ rows, actions }: { rows: Row[]; actions: RowActions }) {
  return (
    <ul className="divide-y rounded-md border">
      {rows.map(({ template, install }) => (
        <li key={template.id} className="hover:bg-accent/30 flex items-center gap-3 px-3 py-2">
          <div
            {...openProps(() => actions.onView({ template, install }))}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 text-left"
          >
            <AgentTile code={template.code} direction={template.direction} imageUrl={template.image} className="size-9 text-xs" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="truncate text-sm font-semibold">{template.name}</span>
                {install ? <PrivateLock scope={install.access_scope} /> : null}
              </div>
              <div className="text-muted-foreground truncate text-xs">
                <code>{template.code}</code>
                {template.summary ? ` · ${template.summary}` : null}
              </div>
            </div>
            <Badge variant="outline" className="hidden shrink-0 font-normal sm:inline-flex">
              {DIRECTION_LABELS[template.direction]}
            </Badge>
            <span className="shrink-0">
              {install ? (
                <InstallStatusBadge status={install.status} />
              ) : (
                <span className="text-muted-foreground text-xs">Not on team</span>
              )}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <AgentActions template={template} install={install} actions={actions} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Responsive grid of agent cards — the whole card opens the detail page. */
function AgentsCards({ rows, actions }: { rows: Row[]; actions: RowActions }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map(({ template, install }) => (
        <Card
          key={template.id}
          {...openProps(() => actions.onView({ template, install }))}
          className="hover:border-primary/40 hover:bg-accent/20 focus-visible:ring-ring flex cursor-pointer flex-col transition-colors focus-visible:ring-1 focus-visible:outline-none"
        >
          <CardHeader>
            <div className="flex items-start gap-3">
              <AgentTile code={template.code} direction={template.direction} imageUrl={template.image} className="size-11 text-sm" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <CardTitle className="min-w-0 truncate text-base">{template.name}</CardTitle>
                  {install ? <PrivateLock scope={install.access_scope} /> : null}
                </div>
                <div className="text-muted-foreground text-xs">
                  <code>{template.code}</code>
                </div>
              </div>
              <Badge variant="outline" className="shrink-0 font-normal">
                {DIRECTION_LABELS[template.direction]}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="mt-auto flex flex-col gap-3">
            {template.summary ? (
              <p className="text-muted-foreground line-clamp-2 text-sm">{template.summary}</p>
            ) : null}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs">
                {install ? (
                  <InstallStatusBadge status={install.status} />
                ) : (
                  <span className="text-muted-foreground">Not on team</span>
                )}
                {install ? (
                  <span className="text-muted-foreground">
                    Enabled: {install.enabled ? 'Yes' : 'No'}
                  </span>
                ) : null}
              </div>
              <AgentActions template={template} install={install} actions={actions} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Cards grouped by direction — section header + count badge, then a card grid. */
function AgentsGroupedByDirection({
  groups,
  actions,
}: {
  groups: { direction: AgentDirection; rows: Row[] }[];
  actions: RowActions;
}) {
  return (
    <div className="space-y-6">
      {groups.map(({ direction, rows: groupRows }) => (
        <section key={direction} aria-labelledby={`agents-direction-${direction}`}>
          <h2
            id={`agents-direction-${direction}`}
            className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
          >
            {DIRECTION_LABELS[direction]}
            <Badge variant="outline" className="text-[10px]">
              {groupRows.length}
            </Badge>
          </h2>
          <AgentsCards rows={groupRows} actions={actions} />
        </section>
      ))}
    </div>
  );
}

function AgentsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
